import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { 
  CreditCard, Calendar, FileText, ArrowLeft, Download,
  CheckCircle, Crown, TrendingUp, Sparkles, Zap, Pause,
  Play, AlertTriangle, Loader2, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_DETAILS = {
  trial: { name: "Free Trial", icon: Zap, color: "text-yellow-500", price: 0, quotes: 3 },
  payg: { name: "Pay As You Go", icon: Zap, color: "text-yellow-500", price: 9, quotes: 0 },
  starter: { name: "Starter", icon: TrendingUp, color: "text-blue-500", price: 59, quotes: 25 },
  professional: { name: "Professional", icon: Crown, color: "text-primary", price: 99, quotes: 75 },
  business: { name: "Business", icon: Sparkles, color: "text-purple-500", price: 150, quotes: 150 },
  pilot: { name: "Pilot", icon: Crown, color: "text-green-500", price: 0, quotes: "Unlimited" }
};

const Subscription = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchBillingInfo();
    fetchInvoices();
    
    // Check for pause success
    if (searchParams.get('paused') === 'true') {
      toast.success("Subscription paused successfully");
    }
    
    // Check for upgrade success
    const upgradedPlan = searchParams.get('upgraded');
    if (upgradedPlan) {
      toast.success(`Successfully upgraded to ${upgradedPlan.charAt(0).toUpperCase() + upgradedPlan.slice(1)} plan!`);
    }
  }, [searchParams]);

  const fetchBillingInfo = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/billing-info`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
      }
    } catch (error) {
      console.error("Failed to fetch billing info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/invoices`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    }
  };

  const handleUpgrade = async (planId) => {
    setActionLoading(planId);
    try {
      const response = await fetch(`${API}/subscriptions/change-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ new_plan_id: planId })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          // Show prorate info before redirect
          const isUpgrade = data.prorate_amount < PLAN_DETAILS[planId]?.price;
          if (isUpgrade && billingInfo?.current_plan && !['trial', 'payg'].includes(billingInfo.current_plan)) {
            toast.info(`Prorated charge: £${data.prorate_amount.toFixed(2)} for remaining billing period`);
          }
          setTimeout(() => {
            window.location.href = data.checkout_url;
          }, 1500);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to change plan");
      }
    } catch (error) {
      toast.error("Failed to change plan");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async () => {
    setActionLoading('pause');
    try {
      const response = await fetch(`${API}/subscriptions/pause`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to pause subscription");
      }
    } catch (error) {
      toast.error("Failed to pause subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async () => {
    setActionLoading('resume');
    try {
      const response = await fetch(`${API}/subscriptions/resume`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Subscription resumed!");
        fetchBillingInfo();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to resume subscription");
      }
    } catch (error) {
      toast.error("Failed to resume subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel? Consider pausing instead to keep your data.")) {
      return;
    }
    
    setActionLoading('cancel');
    try {
      const response = await fetch(`${API}/subscriptions/cancel`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Subscription cancelled. You can still use remaining quotes this period.");
        fetchBillingInfo();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const response = await fetch(`${API}/invoices/${invoice.invoice_id}/pdf`, {
        credentials: "include"
      });
      
      if (!response.ok) {
        throw new Error("Failed to download invoice");
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TradeOffice_Invoice_${invoice.invoice_id.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download invoice");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const planInfo = PLAN_DETAILS[billingInfo?.current_plan] || PLAN_DETAILS.trial;
  const PlanIcon = planInfo.icon;
  const isPaidPlan = !['trial', 'payg'].includes(billingInfo?.current_plan);
  const isPaused = billingInfo?.is_paused;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <h1 className="text-3xl font-bold mb-2">Subscription Management</h1>
        <p className="text-muted-foreground mb-8">Manage your plan, billing, and invoices</p>

        {/* Current Plan Status */}
        <Card className="mb-6" data-testid="current-plan-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isPaidPlan ? 'bg-primary/20' : 'bg-secondary'
                }`}>
                  <PlanIcon className={`w-6 h-6 ${planInfo.color}`} />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {planInfo.name}
                    {isPaused && <Badge variant="secondary">Paused</Badge>}
                  </CardTitle>
                  <CardDescription>
                    {planInfo.quotes === "Unlimited" ? "Unlimited quotes" : `${planInfo.quotes} quotes/month`}
                  </CardDescription>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">
                  £{billingInfo?.monthly_amount || planInfo.price}
                  {isPaidPlan && <span className="text-base font-normal text-muted-foreground">/month</span>}
                </div>
              </div>
            </div>
          </CardHeader>
          {isPaidPlan && (
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                {/* Next Billing Date */}
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Next Billing Date</p>
                    <p className="font-medium">
                      {billingInfo?.next_billing_date || "N/A"}
                    </p>
                    {billingInfo?.next_billing_date && (
                      <p className="text-xs text-muted-foreground">
                        Your next payment of £{billingInfo?.monthly_amount} is due
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Payment Method */}
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Method</p>
                    {billingInfo?.payment_method ? (
                      <p className="font-medium">
                        {billingInfo.payment_method.brand} ending in {billingInfo.payment_method.last4}
                      </p>
                    ) : (
                      <p className="text-muted-foreground">No card on file</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Plan Management */}
        <Card className="mb-6" data-testid="plan-management-card">
          <CardHeader>
            <CardTitle>Plan Management</CardTitle>
            <CardDescription>Upgrade, downgrade, or manage your subscription</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Upgrade/Downgrade Options */}
              <div className="grid md:grid-cols-3 gap-3">
                {['starter', 'professional', 'business'].map((planId) => {
                  const plan = PLAN_DETAILS[planId];
                  const isCurrent = billingInfo?.current_plan === planId;
                  const Icon = plan.icon;
                  
                  return (
                    <Button
                      key={planId}
                      variant={isCurrent ? "secondary" : "outline"}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${isCurrent ? 'border-primary' : ''}`}
                      onClick={() => !isCurrent && handleUpgrade(planId)}
                      disabled={isCurrent || actionLoading === planId}
                      data-testid={`plan-${planId}-btn`}
                    >
                      {actionLoading === planId ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Icon className={`w-5 h-5 ${plan.color}`} />
                      )}
                      <span className="font-medium">{plan.name}</span>
                      <span className="text-sm text-muted-foreground">£{plan.price}/mo</span>
                      {isCurrent && <Badge variant="outline" className="mt-1">Current</Badge>}
                    </Button>
                  );
                })}
              </div>

              <Separator />

              {/* Pause / Resume / Cancel */}
              {isPaidPlan && (
                <div className="flex flex-wrap gap-3">
                  {isPaused ? (
                    <Button
                      variant="default"
                      onClick={handleResume}
                      disabled={actionLoading === 'resume'}
                      data-testid="resume-btn"
                    >
                      {actionLoading === 'resume' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Resume Subscription
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handlePause}
                      disabled={actionLoading === 'pause'}
                      data-testid="pause-btn"
                    >
                      {actionLoading === 'pause' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Pause className="w-4 h-4 mr-2" />
                      )}
                      Pause for 30 Days (£5)
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleCancel}
                    disabled={actionLoading === 'cancel'}
                    data-testid="cancel-btn"
                  >
                    {actionLoading === 'cancel' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-2" />
                    )}
                    Cancel Subscription
                  </Button>
                </div>
              )}

              {!isPaidPlan && (
                <div className="text-center p-4 bg-primary/5 rounded-lg">
                  <p className="text-muted-foreground mb-3">
                    Upgrade to a paid plan to unlock more quotes and features
                  </p>
                  <Button onClick={() => navigate('/pricing')} data-testid="view-plans-btn">
                    View Plans
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoice History */}
        <Card data-testid="invoice-history-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice History
            </CardTitle>
            <CardDescription>Download invoices for your records</CardDescription>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No invoices yet</p>
                <p className="text-sm">Invoices will appear here after your first payment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice, index) => (
                  <div 
                    key={invoice.invoice_id || index}
                    className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg"
                    data-testid={`invoice-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium">{invoice.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {invoice.date_formatted || invoice.date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold">£{invoice.amount}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => downloadInvoice(invoice)}
                        data-testid={`download-invoice-${index}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Subscription;
